import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, BanknoteIcon, CheckCircle2Icon, ChevronDownIcon, ChevronRightIcon, DownloadIcon, Loader2Icon, ReceiptTextIcon } from 'lucide-react';
import { cn, formatCurrency } from '../../../lib/utils';
import { buildCourierReport, setExpressDeliveryPaid, type ExpressAssignmentRow } from '../../../lib/express-hooks';

// ──────────────────────────────────────────────────────────────────
// Reporte de pago a repartidores del periodo seleccionado.
//
// Los montos salen del SNAPSHOT guardado al asignar, no del pedido: si
// el costo de envío se corrigió después, el reporte histórico NO cambia.
// ──────────────────────────────────────────────────────────────────

interface ExpressPayoutReportProps {
  restaurantId: number | string;
  rows: ExpressAssignmentRow[];
  loading: boolean;
  startDate: string;
  endDate: string;
  onChanged: () => void;
}
const fmtDateCR = (iso: string | null) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-CR', {
      timeZone: 'America/Costa_Rica',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
};
export function ExpressPayoutReport({
  restaurantId,
  rows,
  loading,
  startDate,
  endDate,
  onChanged
}: ExpressPayoutReportProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Filtros de consulta. No modifican datos: solo acotan lo que se ve,
  // lo que suman los totales y lo que se exporta al CSV.
  const [courierFilter, setCourierFilter] = useState<string>('all');
  const [payFilter, setPayFilter] = useState<'all' | 'paid' | 'pending'>('all');

  /** Nombre del repartidor de una entrega, venga del catálogo o escrito a mano. */
  const rowCourierName = (row: ExpressAssignmentRow) => (row.delivery?.courier_name || row.order.delivery_driver_name || '').trim();

  // Lista de repartidores presentes en el periodo, para el desplegable.
  const courierOptions = useMemo(() => {
    const names = new Set<string>();
    for (const row of rows) {
      const name = rowCourierName(row);
      if (name) names.add(name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      const name = rowCourierName(row);
      if (!name) return false; // sin repartidor: no entra al reporte
      if (courierFilter !== 'all' && name.toLowerCase() !== courierFilter.toLowerCase()) {
        return false;
      }
      if (payFilter === 'all') return true;
      const paid = Boolean(row.delivery?.paid_at);
      return payFilter === 'paid' ? paid : !paid;
    });
  }, [rows, courierFilter, payFilter]);
  const filtersActive = courierFilter !== 'all' || payFilter !== 'all';
  const report = useMemo(() => buildCourierReport(filteredRows), [filteredRows]);
  const totals = useMemo(() => report.reduce((acc, r) => ({
    deliveries: acc.deliveries + r.deliveries,
    total: acc.total + r.total,
    paid: acc.paid + r.paid,
    pending: acc.pending + r.pending
  }), {
    deliveries: 0,
    total: 0,
    paid: 0,
    pending: 0
  }), [report]);
  const toggle = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);else next.add(key);
      return next;
    });
  };

  // Marcar una entrega individual. La operación es idempotente: si ya
  // estaba en ese estado, la fecha original NO se pisa.
  const handleTogglePaid = async (row: ExpressAssignmentRow) => {
    if (!row.delivery) return;
    const key = `d:${row.delivery.id}`;
    setBusyKey(key);
    setError(null);
    const result = await setExpressDeliveryPaid({
      restaurantId,
      deliveryId: row.delivery.id,
      paid: !row.delivery.paid_at
    });
    setBusyKey(null);
    if (!result.success) {
      setError(result.error || 'No se pudo actualizar el pago.');
      return;
    }
    onChanged();
  };

  // Liquidar de una vez todo lo pendiente de un repartidor en el
  // periodo. Solo toca las entregas SIN pagar; las ya pagadas conservan
  // su fecha original.
  const handlePayGroup = async (key: string, courierName: string, groupRows: ExpressAssignmentRow[], pending: number) => {
    const unpaid = groupRows.filter(r => r.delivery && !r.delivery.paid_at);
    if (unpaid.length === 0) return;
    const ok = window.confirm(`Marcar como pagadas ${unpaid.length} entrega${unpaid.length === 1 ? '' : 's'} de ${courierName}` + ` por ${formatCurrency(pending)}?\n\nEsto solo registra el pago al repartidor. No afecta la caja ni las ventas.`);
    if (!ok) return;
    setBusyKey(key);
    setError(null);
    for (const row of unpaid) {
      const result = await setExpressDeliveryPaid({
        restaurantId,
        deliveryId: (row.delivery as NonNullable<typeof row.delivery>).id,
        paid: true
      });
      if (!result.success) {
        setBusyKey(null);
        setError(result.error || 'No se pudo completar el pago del grupo.');
        onChanged();
        return;
      }
    }
    setBusyKey(null);
    onChanged();
  };
  const handleExport = () => {
    const lines: string[] = ['Repartidor,Pedido,Fecha,Monto,Estado'];
    for (const group of report) {
      for (const row of group.rows) {
        const amount = row.delivery ? row.delivery.payout_amount : row.order.delivery_fee;
        const estado = row.delivery?.paid_at ? 'Pagado' : 'Pendiente';
        const fecha = fmtDateCR(row.order.created_at);
        const pedido = row.order.order_number || row.order.id;
        lines.push([`"${group.courierName.replace(/"/g, '""')}"`, `"${pedido}"`, `"${fecha}"`, amount, estado].join(','));
      }
    }
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `servicio-express_${startDate}_a_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return <section aria-labelledby="express-report-heading" className="bg-white rounded-2xl border border-gray-200" data-id="element-838" data-mp="oxoz9s3uhtnf">
      <header className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3" data-id="element-839" data-mp="xwiupf5fwdze">
        <div className="flex items-center gap-2 min-w-0" data-id="element-840" data-mp="12mz9b2kzy5nc">
          <ReceiptTextIcon className="w-5 h-5 text-gray-700 shrink-0" data-id="element-841" data-mp="12ok48tb83qlq" />
          <div className="min-w-0" data-id="element-842" data-mp="573hqju7sb0u">
            <h2 id="express-report-heading" className="font-bold text-gray-900" data-id="element-843" data-mp="55sc0eh7kt73">
              Reporte de pagos
            </h2>
            <p className="text-xs text-gray-500" data-id="element-844" data-mp="2n6xji01xvhj">
              {startDate} a {endDate} · hora de Costa Rica
            </p>
          </div>
        </div>
        <button onClick={handleExport} disabled={report.length === 0} className="h-10 px-4 rounded-xl font-bold text-sm bg-white border border-gray-300 text-gray-700 hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all flex items-center gap-2" data-id="element-845" data-mp="3tujblo5n1st8">
          <DownloadIcon className="w-4 h-4" data-id="element-846" data-mp="3b38prlers687" />
          Exportar CSV
        </button>
      </header>

      {error && <div role="alert" className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" data-id="element-847" data-mp="363m88zrws3tl">
          <AlertTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" data-id="element-848" data-mp="313843stfhe0u" />
          <span data-id="element-849" data-mp="2x2xyih9jspmi">{error}</span>
        </div>}

      {/* Filtros de consulta */}
      <div className="px-5 pt-4 flex flex-wrap items-center gap-2" data-id="element-850" data-mp="2s5pcu6p6y3gi">
        <label className="sr-only" htmlFor="express-courier-filter" data-id="element-851" data-mp="jv6gs1d4l69t">
          Filtrar por repartidor
        </label>
        <select id="express-courier-filter" value={courierFilter} onChange={e => setCourierFilter(e.target.value)} className="h-10 px-3 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-800 focus:outline-none focus:border-gray-900" data-id="element-852" data-mp="otq2c0nt4g1n">
          <option value="all" data-id="element-853" data-mp="14exo83c61i8">Todos los repartidores</option>
          {courierOptions.map(name => <option key={name} value={name} data-id="element-854" data-mp="5wovsbelu7vl">
              {name}
            </option>)}
        </select>

        <div role="group" aria-label="Filtrar por estado de pago" className="inline-flex rounded-xl border border-gray-300 overflow-hidden" data-id="element-855" data-mp="3ne825rn66a8b">
          {([['all', 'Todos'], ['paid', 'Pagado'], ['pending', 'Pendiente']] as const).map(([value, label]) => <button key={value} onClick={() => setPayFilter(value)} aria-pressed={payFilter === value} className={cn('h-10 px-3 text-sm font-bold transition-colors', payFilter === value ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50')} data-id="element-856" data-mp="2zpt29uh25ns0">
              {label}
            </button>)}
        </div>

        {filtersActive && <button onClick={() => {
        setCourierFilter('all');
        setPayFilter('all');
      }} className="h-10 px-3 rounded-xl text-sm font-bold text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors" data-id="element-857" data-mp="3do9iqd4q2snc">
            Limpiar filtros
          </button>}
      </div>

      <div className="p-5" data-id="element-858" data-mp="a69rvg1wuod">
        {loading ? <div className="flex justify-center py-12 text-gray-400" data-id="element-859" data-mp="bhcrt1o9yef">
            <Loader2Icon className="w-6 h-6 animate-spin" data-id="element-860" data-mp="1zxbmw92p0lf7" />
          </div> : report.length === 0 ? <div className="text-center py-12" data-id="element-861" data-mp="1v5bs0v1hciph">
            <ReceiptTextIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" data-id="element-862" data-mp="1v4iixnf1th46" />
            <p className="font-bold text-gray-900" data-id="element-863" data-mp="1q7junli2g4dw">
              {filtersActive ? 'Sin entregas para este filtro' : 'No hubo entregas asignadas en este periodo'}
            </p>
            <p className="text-sm text-gray-500 mt-1" data-id="element-864" data-mp="2itlbhqb80udg">
              {filtersActive ? 'Probá con otro repartidor o estado de pago.' : 'Probá ampliando el rango de fechas.'}
            </p>
          </div> : <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4" data-id="element-865" data-mp="23xvlzi6ttz3z">
              <SummaryTile label="Entregas" value={String(totals.deliveries)} data-id="element-866" data-mp="xiuzs47lsval" />
              <SummaryTile label="Monto total" value={formatCurrency(totals.total)} data-id="element-867" data-mp="siqdtynxf8cz" />
              <SummaryTile label="Ya pagado" value={formatCurrency(totals.paid)} tone="paid" data-id="element-868" data-mp="3450ok65p17l2" />
              <SummaryTile label="Pendiente" value={formatCurrency(totals.pending)} tone="pending" data-id="element-869" data-mp="3e3zxlpudf58l" />
            </div>

            <ul className="space-y-2" data-id="element-870" data-mp="3nvm79hr28w98">
              {report.map(group => {
            const key = group.courierId ? `c:${group.courierId}` : `legacy:${group.courierName}`;
            const isOpen = expanded.has(key);
            return <li key={key} className="rounded-xl border border-gray-200 overflow-hidden" data-id="element-871" data-mp="5prdl65wzms1">
                    <div className="flex items-center gap-2 pr-3" data-id="element-872" data-mp="22jm6tx2hd1ce">
                      <button onClick={() => toggle(key)} aria-expanded={isOpen} className="flex-1 min-w-0 px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors" data-id="element-873" data-mp="27hdx6mnh8feh">
                        {isOpen ? <ChevronDownIcon className="w-4 h-4 text-gray-400 shrink-0" data-id="element-874" data-mp="29fdzkj601w56" /> : <ChevronRightIcon className="w-4 h-4 text-gray-400 shrink-0" data-id="element-875" data-mp="24hcg3x4nvut6" />}
                        <div className="min-w-0 flex-1" data-id="element-876" data-mp="2ed5rveak3k9w">
                          <p className="font-bold text-sm text-gray-900 truncate" data-id="element-877" data-mp="2ecnu2d88wxzv">
                            {group.courierName}
                            {group.legacy && <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600" data-id="element-878" data-mp="2n6itd4moru7l">
                                Sin vincular
                              </span>}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-879" data-mp="3gpqcng33tzop">
                            {group.deliveries} entrega
                            {group.deliveries === 1 ? '' : 's'}
                          </p>
                        </div>
                        <div className="text-right shrink-0" data-id="element-880" data-mp="36qhmkybd157m">
                          <p className="text-base font-black text-gray-900 tabular-nums" data-id="element-881" data-mp="3bq425eenuxjl">
                            {formatCurrency(group.total)}
                          </p>
                          <p className="text-xs tabular-nums" data-id="element-882" data-mp="2wzn1hx9uobnr">
                            <span className="text-emerald-700" data-id="element-883" data-mp="31tr6wc3gwbmq">
                              {formatCurrency(group.paid)} pagado
                            </span>
                            {' · '}
                            <span className="text-amber-700" data-id="element-884" data-mp="2t0e3gg7s1rme">
                              {formatCurrency(group.pending)} pend.
                            </span>
                          </p>
                        </div>
                      </button>

                      {/* Liquidar todo lo pendiente del repartidor. Las
                          entregas sin vincular no se pueden marcar: no
                          tienen registro propio, vienen del flujo viejo. */}
                      {!group.legacy && group.pending > 0 && <button onClick={() => handlePayGroup(key, group.courierName, group.rows, group.pending)} disabled={busyKey === key} className="h-10 px-3 shrink-0 rounded-lg font-bold text-sm bg-gray-900 text-white hover:bg-gray-800 active:scale-95 transition-all disabled:opacity-40 flex items-center gap-2" data-id="element-885" data-mp="37npvt9i21was">
                          {busyKey === key ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-886" data-mp="mve5wmv95cv4" /> : <BanknoteIcon className="w-4 h-4" data-id="element-887" data-mp="uf5la4g8345a" />}
                          Marcar pagado
                        </button>}
                      {!group.legacy && group.pending === 0 && <span className="shrink-0 text-xs font-bold px-2 py-1 rounded bg-emerald-50 text-emerald-700 flex items-center gap-1" data-id="element-888" data-mp="194utgjmqg8f2">
                          <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-889" data-mp="bkiayrdfse8d" />
                          Al día
                        </span>}
                    </div>

                    {isOpen && <div className="border-t border-gray-100 divide-y divide-gray-50" data-id="element-890" data-mp="qf4y9vf25kmm">
                        {group.rows.map(row => {
                  const amount = row.delivery ? row.delivery.payout_amount : row.order.delivery_fee;
                  const paid = Boolean(row.delivery?.paid_at);
                  return <div key={row.order.id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm" data-id="element-891" data-mp="3sz4x84gkqyun">
                              <div className="min-w-0" data-id="element-892" data-mp="6sgegw6924iz">
                                <p className="font-bold text-gray-900 tabular-nums" data-id="element-893" data-mp="3f78725yguz0w">
                                  #{row.order.order_number || row.order.id}
                                </p>
                                <p className="text-xs text-gray-500 truncate" data-id="element-894" data-mp="3a6atk6oeit5q">
                                  {fmtDateCR(row.order.created_at)}
                                  {row.order.customer_name ? ` · ${row.order.customer_name}` : ''}
                                </p>
                              </div>
                              <div className="flex items-center gap-3 shrink-0" data-id="element-895" data-mp="2rfjj11sogwpr">
                                <div className="text-right" data-id="element-896" data-mp="2hce9og0131gr">
                                  <p className="font-bold text-gray-900 tabular-nums" data-id="element-897" data-mp="2m4nuxiq77dcp">
                                    {formatCurrency(amount)}
                                  </p>
                                  <p className={cn('text-xs font-medium', paid ? 'text-emerald-700' : 'text-amber-700')} data-id="element-898" data-mp="2ce2zu59qgqyj">
                                    {paid ? `Pagado${row.delivery?.paid_at ? ` · ${fmtDateCR(row.delivery.paid_at)}` : ''}` : 'Pendiente'}
                                  </p>
                                </div>
                                {row.delivery && <button onClick={() => handleTogglePaid(row)} disabled={busyKey === `d:${row.delivery.id}`} title={paid ? 'Revertir a pendiente' : 'Marcar esta entrega como pagada'} className={cn('h-9 px-3 rounded-lg font-bold text-xs border active:scale-95 transition-all disabled:opacity-40 flex items-center gap-1.5', paid ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400')} data-id="element-899" data-mp="r0ox251tpg1r">
                                    {busyKey === `d:${row.delivery.id}` ? <Loader2Icon className="w-3.5 h-3.5 animate-spin" data-id="element-900" data-mp="3udhh7e57f8va" /> : paid ? <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-901" data-mp="84pzuxfnvd3a" /> : <BanknoteIcon className="w-3.5 h-3.5" data-id="element-902" data-mp="cwrakex6kvr7" />}
                                    {paid ? 'Pagado' : 'Marcar'}
                                  </button>}
                              </div>
                            </div>;
                })}
                      </div>}
                  </li>;
          })}
            </ul>
          </>}
      </div>
    </section>;
}
function SummaryTile({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone?: 'paid' | 'pending';
}) {
  return <div className={cn('p-3 rounded-xl border', tone === 'paid' ? 'border-emerald-200 bg-emerald-50' : tone === 'pending' ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white')} data-id="element-903" data-mp="twyw1z8wz0j7">
      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide" data-id="element-904" data-mp="12vu83ryn34uf">
        {label}
      </p>
      <p className={cn('text-lg font-black tabular-nums mt-0.5', tone === 'paid' ? 'text-emerald-800' : tone === 'pending' ? 'text-amber-800' : 'text-gray-900')} data-id="element-905" data-mp="17qzqwhwqw3ea">
        {value}
      </p>
    </div>;
}